
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { NewClientFormData } from "../types";

export const useNewClientForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<NewClientFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStateChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      state: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      console.log("Submitting client data:", formData);
      
      // Validate form data
      if (!formData.firstName.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "First name is required",
        });
        setIsSubmitting(false);
        return;
      }

      if (!formData.lastName.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Last name is required",
        });
        setIsSubmitting(false);
        return;
      }

      if (!formData.email.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Email is required",
        });
        setIsSubmitting(false);
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('create-client', {
        body: { 
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          street: formData.street || null,
          city: formData.city || null,
          state: formData.state || null,
          zipCode: formData.zipCode || null
        }
      });

      if (error) {
        console.error('Error creating client:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to create client",
        });
        setIsSubmitting(false);
        return;
      }

      console.log('Client creation response:', data);
      
      // Check specifically if the email was sent and show appropriate message
      if (data.emailSent) {
        toast({
          variant: "default",
          title: "Success!",
          description: `${formData.firstName} ${formData.lastName} was added successfully and welcome email was sent`,
          duration: 5000,
        });
      } else {
        toast({
          variant: "default",
          title: "Success with warning",
          description: `${formData.firstName} ${formData.lastName} was added successfully but welcome email failed to send`,
          duration: 5000,
        });
        
        // Log error details if available
        if (data.emailError) {
          console.error('Welcome email error:', data.emailError);
        }
      }
      
      // Navigate to clients page after successful creation
      setTimeout(() => {
        navigate("/admin/clients");
      }, 1000);
      
    } catch (error: any) {
      console.error('Error in handleSubmit:', error);
      
      // Extract error message
      let errorMessage = "An unexpected error occurred";
      
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          errorMessage = String(error.message);
        } else if (error.error) {
          errorMessage = String(error.error);
        }
      }
      
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    handleInputChange,
    handleStateChange,
    handleSubmit,
    isSubmitting
  };
};
